/* StyleUtil.js */

"use strict";

const _ = require("lodash");
const {v4: uuidv4} = require("uuid");

class StyleUtil {
    static getCssStyleString(styleObj) {
        let cssStylesString = Object.keys(styleObj)
            .map((key) => {
                return key + ": " + styleObj[key] + ";";
            })
            .join(" ");
        return cssStylesString;
    }

    static getCssClassString(classArray) {
        return classArray ? classArray.join(" ") : "";
    }

    static appendClassListToElement(classList, containingElement) {
        for (let i = 0; i < classList.length; i++) {
            containingElement.classList.add(classList[i]);
        }
    }

    static processContainerLayoutConfig(
        layoutSettings,
        styleElement,
        inheritLayoutDefaultValue,
        fieldName
    ) {
        let layoutCss = {};
        let layoutType = _.has(layoutSettings, "flex-container")
            ? "flex"
            : _.has(layoutSettings, "grid-container")
            ? "grid"
            : null;
        if (layoutType) {
            layoutCss = {display: layoutType};
            let layoutJtypeConfig = _.get(layoutSettings, layoutType + "-container");
            Object.keys(layoutJtypeConfig).forEach((propName) => {
                if (propName !== "inlineLabels") {
                    layoutCss[propName] = layoutJtypeConfig[propName];
                }
            });
        }
        let layoutCssClassNamePrefix = `jTypeClass_layout_container_${fieldName}`;
        let layoutCssClasses = _.isEmpty(layoutCss)
            ? []
            : [
                  StyleUtil.createCssClassGivenCssStyles(
                      layoutCss,
                      layoutCssClassNamePrefix,
                      styleElement
                  ),
              ];
        let inheritLayout = _.get(layoutSettings, "inheritLayout", inheritLayoutDefaultValue);
        return {css: layoutCss, cssClasses: layoutCssClasses, inheritLayout: inheritLayout};
    }

    /**
     * Create new css class with unique name with given css, create new style sheet element with this new css class, add it to the given style element
     * @param css - raw css used to create the css class
     * @param classNamePrefix - css class name prefix to be used in the new css class name
     * @param styleElement - style sheet DOM element to add new css class definition to
     * @param rawCss {boolean} - if true, css param is a string else css param is object of key value paris with raw css styles
     * @returns css class name (new css class created by this function with given css)
     */
    static createCssClassGivenCssStyles(css, classNamePrefix, styleElement, rawCss = false) {
        if (_.isEmpty(css)) {
            return;
        }
        let uniqueCssClassName =
            (classNamePrefix ? classNamePrefix : "jTypeClass") + "_" + uuidv4();
        uniqueCssClassName = uniqueCssClassName.replaceAll("@", "");
        styleElement.innerText += `.${uniqueCssClassName} { ${
            rawCss ? css : StyleUtil.getCssStyleString(css)
        } }`;

        return uniqueCssClassName;
    }

    /**
     * Given inline Config, creates css class with given config & returns the inline class name
     * @param inlineLabelsConfig - jType inline Labels config
     * @param styleElement - DOM Style element to add new css classes
     * @param fieldName - field name to use in generation of unique class name
     * @returns {string} - css class name
     */
    static createInlineStyleGivenConfig(inlineLabelsConfig, styleElement, fieldName) {
        if (_.isEmpty(inlineLabelsConfig) || !_.isObject(inlineLabelsConfig)) {
            return "";
        }
        let uniqueCssClassName = `jTypeClass_inline_${fieldName}_${uuidv4()}`;
        let selectOptionsClassSelector = `gl-form-renderer .rendered_control.${uniqueCssClassName} > .form_entry > .select_options_header`;
        let styleCss = _.has(inlineLabelsConfig, "labelWidth")
            ? `gl-form-renderer .rendered_control.${uniqueCssClassName} > label, ${selectOptionsClassSelector} > label { width: ${inlineLabelsConfig.labelWidth};}\n`
            : "";
        styleCss += _.has(inlineLabelsConfig, "fieldWidth")
            ? `gl-form-renderer .rendered_control.${uniqueCssClassName} > :nth-child(2), ${selectOptionsClassSelector} > gl-multi-select { width: ${inlineLabelsConfig.fieldWidth}; flex-grow: unset;}\n`
            : "";
        if (!_.isEmpty(styleCss)) {
            styleElement.innerText += styleCss;
            return uniqueCssClassName;
        }
        return "";
    }

    /**
     * Given inlineLabels config, processes it & it returns a list of classes
     * @param jTypeLayoutSettings - layout config defined in jType
     * @param styleElement - style DOM element to add new css class
     * @param defaultInlineClasses - default inline class if no custom configuration defined in jType
     * @param inlineLabelsEnabledByDefault {boolean} - indicates if inline labels are enabled by default
     * @param fieldName {string} - field name
     * @returns {*[]}
     */
    static processInlineLabelsConfig(
        jTypeLayoutSettings,
        styleElement,
        defaultInlineClasses,
        inlineLabelsEnabledByDefault,
        fieldName
    ) {
        let inlineLabelsConfig = _.get(
            jTypeLayoutSettings,
            "inlineLabels",
            inlineLabelsEnabledByDefault
        );
        let inlineLabelsClassList = [...defaultInlineClasses];
        if (inlineLabelsConfig === true) {
        } else if (inlineLabelsConfig === false) {
            inlineLabelsClassList = [];
        } else {
            let inlineClasses = StyleUtil.createInlineStyleGivenConfig(
                inlineLabelsConfig,
                styleElement,
                fieldName
            );
            if (!_.isEmpty(inlineClasses)) {
                inlineLabelsClassList.push(inlineClasses);
            }
        }
        return inlineLabelsClassList;
    }

    /**
     * Create new style element and attach to parent element
     * @param parentElement
     * @returns {HTMLElement}
     */
    static createStyleSheetElement(parentElement) {
        let styleElement = document.createElement("style");
        styleElement.type = "text/css";
        parentElement.append(styleElement);
        return styleElement;
    }

    /**
     * Process given layout config and return an object with layout css classes, item layout classes,
     * @param configSettings - object with layout config
     * @param parentJTypeContext - parent settings to consider when calculating element level layout settings. By default parent layout settings are inherited by element.
     * @param defaultInlineClass - default inline class to use if no custom inline config
     * @param fieldName - current control adapter name
     * @returns {{layout: {container: {css: {}, cssClasses: Array}, item: {cssClass: Array}}}}
     */
    static processLayoutConfig(configSettings, parentJTypeContext, defaultInlineClass, fieldName) {
        let displayLayoutPropertyPath = "Display.layout";
        let jTypeContext = {layout: {container: {css: {}, cssClasses: []}, item: {cssClass: []}}};

        jTypeContext.styleElement = parentJTypeContext.styleElement;
        if (fieldName === "@Complete") {
            displayLayoutPropertyPath = _.has(configSettings, "allType")
                ? "allType.@Display.layout"
                : "options.@Display.layout";
        }
        let elementJTypeLayoutSettings = _.get(configSettings, displayLayoutPropertyPath, {});
        let defaultInlineClassList = _.isEmpty(parentJTypeContext.layout.inlineLabels)
            ? [defaultInlineClass]
            : [...parentJTypeContext.layout.inlineLabels];
        jTypeContext.layout.inlineLabels = StyleUtil.processInlineLabelsConfig(
            elementJTypeLayoutSettings,
            parentJTypeContext.styleElement,
            defaultInlineClassList,
            !_.isEmpty(parentJTypeContext.layout.inlineLabels),
            fieldName
        );

        jTypeContext.layout.container = StyleUtil.processContainerLayoutConfig(
            elementJTypeLayoutSettings,
            jTypeContext.styleElement,
            parentJTypeContext.layout.container.inheritLayout,
            fieldName
        );
        if (jTypeContext.layout.container.inheritLayout) {
            if (
                _.isEmpty(jTypeContext.layout.container.css) &&
                _.has(parentJTypeContext, "layout.container.css.display")
            ) {
                jTypeContext.layout.container.css = {
                    display: parentJTypeContext.layout.container.css.display,
                };
            }
            if (
                _.get(parentJTypeContext, "layout.container.css.display", "") ===
                _.get(jTypeContext, "layout.container.css.display", "")
            ) {
                jTypeContext.layout.container.cssClasses = _.concat(
                    parentJTypeContext.layout.container.cssClasses,
                    jTypeContext.layout.container.cssClasses
                );
            }
        }
        jTypeContext.layout.item.cssClass = StyleUtil.processItemLayoutConfig(
            elementJTypeLayoutSettings,
            parentJTypeContext,
            fieldName
        );
        return jTypeContext;
    }

    static processItemLayoutConfig(elementJTypeLayoutSettings, parentJTypeContext, fieldName) {
        let containerLayoutType = _.get(parentJTypeContext, "layout.container.css.display", "");
        let itemLayoutCss = {};
        let layoutJtypeConfig = _.get(
            elementJTypeLayoutSettings,
            containerLayoutType + "-item",
            {}
        );
        Object.keys(layoutJtypeConfig).forEach((propName) => {
            itemLayoutCss[propName] = layoutJtypeConfig[propName];
        });
        let cssClassNamePrefix = `jTypeClass_layout_item_${fieldName}`;
        let itemCssClass = StyleUtil.createCssClassGivenCssStyles(
            itemLayoutCss,
            cssClassNamePrefix,
            parentJTypeContext.styleElement
        );
        return itemCssClass;
    }

    static renderStylesConfig(stylesConfig, containingElem) {
        let styles = StyleUtil.getCssStyleString(_.get(stylesConfig, "cssStyle", {}));
        containingElem.style.cssText += styles;
        let cssClasses = _.get(stylesConfig, "cssClasses", []);
        StyleUtil.appendClassListToElement(cssClasses, containingElem);
    }

    static clearJTypeClassesDynamicallyAdded(domElement, stylesConfig, layoutClasses) {
        let domElementStyleText = domElement.style.cssText;
        for (const [key, val] of Object.entries(_.get(stylesConfig, "cssStyle", {}))) {
            domElementStyleText = domElementStyleText.replace(`${key}: ${val};`, "");
        }
        domElement.style.cssText = domElementStyleText;
        let cssClasses = _.get(stylesConfig, "cssClasses", []);
        let domElementClasses = [...domElement.classList];
        domElementClasses.forEach((className) => {
            if (layoutClasses.includes(className) || cssClasses.includes(className)) {
                domElement.classList.remove(className);
            }
        });
    }
}

StyleUtil.DEFAULT_INLINE_CLASS = "inline";

module.exports = StyleUtil;
